import type { jsPDF } from 'jspdf';

/**
 * WinAnsi (CP1252) — die Kodierung, in der die Standardschriften von jsPDF
 * schreiben.
 *
 * ═══ WARUM ES DIESE DATEI GIBT ════════════════════════════════════════════
 * jsPDF bildet jede Zeichenkette ueber die Kodierungstabelle der aktiven
 * Schrift ab (`to8bitStream`). Die vierzehn PDF-Standardschriften — und damit
 * jedes `setFont('helvetica', …)` ohne eingebettete Schrift — koennen genau
 * CP1252. Findet jsPDF darin auch nur EIN Zeichen nicht, faellt es nicht etwa
 * fuer dieses eine Zeichen zurueck, sondern schreibt die GANZE Zeichenkette in
 * UTF-16BE. Die Standardschrift hat dafuer keine Zuordnungstabelle, und der
 * Betrachter liest die Doppelbytes wieder als CP1252:
 *
 *   '└─ Bauherr'  ->  Bytes 25 14 25 00 00 20 00 42 00 61 …
 *                 ->  auf dem Blatt: '%%  B a u h e r r'
 *
 * Genau dieses Bild hat der Betrieb im Kapitel „Struktur" des SiGe-Handbuchs
 * gemeldet: die '%%' sind die Hochbytes der Rahmenzeichen (U+2500/U+2514
 * beginnen beide mit 0x25 = '%'), die zerrissene Buchstabenstellung sind die
 * Nullbytes zwischen den ASCII-Zeichen. EIN Fehler, zwei Symptome.
 *
 * ═══ WAS HIER NICHT PASSIERT ══════════════════════════════════════════════
 * Es wird KEINE Schrift eingebettet. `embeddedFont.ts` tut das fuer Rechnungen
 * (PDF/A-Pflicht) und laedt dafuer rund 600 kB Base64 nach. Handbuch, Aushang
 * und Koordinierungsbericht brauchen kein PDF/A; sie bezahlen diesen Preis
 * nicht. Umlaute, ß, € sowie Gedanken-, Halbgeviertstrich und die deutschen
 * Anfuehrungszeichen liegen ohnehin IN CP1252 und bleiben unangetastet —
 * nachgewiesen in `winAnsi.test.ts`.
 */

/**
 * Die 27 Zeichen, die CP1252 zusaetzlich zu Latin-1 auf den Plaetzen
 * 0x80–0x9F fuehrt. Ohne diese Liste faenden Gedankenstrich, Anfuehrungs-
 * zeichen und Euro-Zeichen kein Zuhause, obwohl jsPDF sie einwandfrei setzt.
 */
const CP1252_ZUSATZ: ReadonlySet<number> = new Set([
    0x20AC, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021, 0x02C6, 0x2030,
    0x0160, 0x2039, 0x0152, 0x017D, 0x2018, 0x2019, 0x201C, 0x201D, 0x2022,
    0x2013, 0x2014, 0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x017E, 0x0178,
]);

/**
 * Laesst sich dieses Zeichen von einer PDF-Standardschrift setzen?
 *
 * Steuerzeichen gelten ausdruecklich als NICHT darstellbar: sie kaemen als
 * leerer Platz oder als Kaestchen aufs Blatt. Zeilenumbruch und Tabulator sind
 * ausgenommen — die verarbeitet jsPDF selbst.
 */
export const istWinAnsiZeichen = (zeichen: string): boolean => {
    const cp = zeichen.codePointAt(0);
    if (cp === undefined) return true;
    if (cp === 0x09 || cp === 0x0A || cp === 0x0D) return true;
    if (cp < 0x20 || (cp >= 0x7F && cp <= 0x9F)) return false;
    if (cp <= 0xFF) return true;
    return CP1252_ZUSATZ.has(cp);
};

/**
 * Feste Entsprechungen fuer die Zeichen, die im Baustellenalltag tatsaechlich
 * auftauchen. Alles andere geht ueber die Akzent-Zerlegung (siehe unten) oder
 * endet als '?'.
 *
 * Bewusst KEIN Ersatz fuer Umlaute: 'ä' -> 'ae' waere eine Verschlimmerung,
 * denn CP1252 kann 'ä'.
 */
const ERSATZ: Readonly<Record<string, string>> = {
    // Rahmen- und Kastenzeichen (U+2500-Block)
    '─': '-', '━': '-', '│': '|', '┃': '|',
    '┌': '+', '┐': '+', '└': '+', '┘': '+',
    '├': '+', '┤': '+', '┬': '+', '┴': '+', '┼': '+',
    '═': '=', '║': '|', '╔': '+', '╗': '+', '╚': '+', '╝': '+',
    '╠': '+', '╣': '+', '╦': '+', '╩': '+', '╬': '+',
    // Pfeile
    '→': '->', '←': '<-', '↑': '^', '↓': 'v', '↔': '<->',
    '⇒': '=>', '⇐': '<=', '⇔': '<=>', '➔': '->', '⟶': '->', '►': '>', '◄': '<',
    // Haken und Kaestchen
    '✓': '+', '✔': '+', '✗': 'x', '✘': 'x', '✕': 'x', '✖': 'x',
    '☐': '[ ]', '☑': '[x]', '☒': '[x]',
    // Aufzaehlungs- und Statuszeichen
    '■': '*', '□': '*', '●': '*', '○': '*', '◆': '*', '◇': '*',
    '▲': '*', '△': '*', '▼': '*', '▽': '*', '★': '*', '☆': '*', '◦': '*',
    '⚡': '*', '⚠': '!', '☠': '!',
    // Rechenzeichen. 'Σ' steht im Gefahrstoffbereich fuer die Summenangabe
    // („Σ 16 PAK") und kommt so auch aus Pruefberichten in die Befunde. Ohne
    // diesen Eintrag stuende auf dem Blatt '? 16 PAK'.
    '≈': '~', '≠': '!=', '≤': '<=', '≥': '>=', '−': '-', '±': '+/-', 'Σ': 'Summe',
    // Striche, die aus Textverarbeitungen mitkommen
    '‑': '-', '‒': '-', '―': '-', '⁄': '/',
    // Zwischenraeume und unsichtbare Steuerzeichen. Als Escape geschrieben,
    // weil sie im Quelltext sonst niemand sieht — und ein Editor sie still
    // verschluckt, sobald jemand die Zeile anfasst.
    '\u2007': ' ', '\u2008': ' ', '\u2009': ' ', '\u200A': ' ', '\u202F': ' ',
    '\u2060': '', '\u200B': '', '\u200C': '', '\u200D': '', '\uFEFF': '',
    '\uFE0E': '', '\uFE0F': '',
    // Kuerzel
    '№': 'Nr.', '℃': '°C', '℉': '°F',
    // Lateinische Buchstaben, die keine Zerlegung haben — polnische,
    // kroatische und maltesische Firmennamen stehen im Verteiler.
    'Ł': 'L', 'ł': 'l', 'Đ': 'D', 'đ': 'd', 'Ħ': 'H', 'ħ': 'h',
};

/**
 * Fuehrt eine Zeichenkette in CP1252 ueber.
 *
 * Der Regelfall ist der Schnellweg: enthaelt der Text nichts Auffaelliges,
 * kommt er unveraendert zurueck. Erst im Fundfall wird umgebaut — und zuerst
 * nach NFC zusammengezogen, damit ein getrennt getipptes 'a' + Trema wirklich
 * als 'ä' aufs Blatt kommt statt als '?'.
 *
 * Diese Funktion ist das Netz fuer FREIEN Text (Projektnamen, Bemerkungen,
 * Gewerkebezeichnungen). Fuer die Textbausteine der Generatoren selbst ist
 * sie NICHT gedacht — die haelt der Waechtertest in `winAnsi.test.ts` sauber,
 * denn ein '->' aus der Ersatztabelle ist ein Notbehelf und keine Typografie.
 */
export const winAnsiSicher = (roh: string): string => {
    if (!roh) return roh;
    let fund = false;
    for (const z of roh) {
        if (!istWinAnsiZeichen(z)) { fund = true; break; }
    }
    if (!fund) return roh;

    let aus = '';
    for (const z of roh.normalize('NFC')) {
        if (istWinAnsiZeichen(z)) { aus += z; continue; }
        const fest = ERSATZ[z];
        if (fest !== undefined) { aus += fest; continue; }
        // Akzent abstreifen statt aufgeben: aus 'Łódź' wird so wenigstens
        // 'Lodz' und nicht '?ód?'. Ein Firmenname bleibt lesbar.
        const nackt = z.normalize('NFD').replace(/\p{M}/gu, '');
        aus += nackt && [...nackt].every(istWinAnsiZeichen) ? nackt : '?';
    }
    return aus;
};

/** Die Zeichen einer Zeichenkette, die eine Standardschrift nicht setzen kann
 *  — jedes nur einmal, in der Reihenfolge des Auftretens. Fuer Waechtertests. */
export const nichtDarstellbareZeichen = (roh: string): string[] =>
    [...new Set([...(roh || '')].filter(z => !istWinAnsiZeichen(z)))];

/** Schon gefilterte Dokumente — ein zweiter Filter waere harmlos, aber unnoetig. */
const gefiltert = new WeakSet<object>();

/**
 * Legt einen Filter VOR `doc.text` dieses einen Dokuments.
 *
 * Warum am Dokument und nicht an jeder Aufrufstelle: die drei Generatoren
 * rufen `doc.text` an ueber vierzig Stellen auf, `jspdf-autotable` und
 * `letterhead.ts` zusaetzlich von innen. Eine einzige vergessene Stelle
 * genuegt, damit ein Absatz wieder als UTF-16-Salat auf dem Blatt landet.
 * Am Dokument kann keine vergessen werden.
 *
 * Das ist die LETZTE Rueckfallebene. Wo die Zeilenbreite zaehlt, wird schon
 * vor `splitTextToSize` gesaeubert (die Normalisierer der Generatoren) —
 * sonst misst jsPDF eine Zeichenkette und setzt eine andere.
 */
export const sichereTextausgabe = (doc: jsPDF): void => {
    const schluessel = doc as unknown as object;
    if (gefiltert.has(schluessel)) return;
    gefiltert.add(schluessel);

    const ziel = doc as unknown as { text: (...args: unknown[]) => jsPDF };
    const original = ziel.text.bind(doc) as (...args: unknown[]) => jsPDF;
    ziel.text = (...args: unknown[]): jsPDF => {
        const t = args[0];
        if (typeof t === 'string') args[0] = winAnsiSicher(t);
        else if (Array.isArray(t)) args[0] = t.map(z => (typeof z === 'string' ? winAnsiSicher(z) : z));
        return original(...args);
    };
};
